import { Fragment, useCallback, useEffect, useState } from 'react'
import {
  clearPlatformAdminToken,
  getPlatformAdminToken,
  platformAdminApi,
  setPlatformAdminToken,
  type PricingUiConfig,
} from '../lib/api'

const PLANS = ['free', 'starter', 'growth', 'enterprise'] as const

export function PlatformAdminPage() {
  const [tokenInput, setTokenInput] = useState('')
  const [authed, setAuthed] = useState(!!getPlatformAdminToken())
  const [error, setError] = useState<string | null>(null)
  const [workspaces, setWorkspaces] = useState<Record<string, unknown>[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [detail, setDetail] = useState<{
    workspace: Record<string, unknown>
    members: Record<string, unknown>[]
  } | null>(null)
  const [pricingUi, setPricingUi] = useState<PricingUiConfig | null>(null)
  const [visibleCsv, setVisibleCsv] = useState('')
  const [highlightName, setHighlightName] = useState('')
  const [promoBadge, setPromoBadge] = useState('')
  const [audit, setAudit] = useState<Record<string, unknown>[]>([])

  const [nudgeStats, setNudgeStats] = useState<{
    byStatus: { status: string; count: number }[]
    approvedByCategory: { category: string; count: number }[]
    totalApproved: number
    totalAll: number
  } | null>(null)
  const [nudgeTab, setNudgeTab] = useState<'pending_review' | 'approved' | 'rejected' | 'draft'>('pending_review')
  const [nudgeList, setNudgeList] = useState<Record<string, unknown>[]>([])
  const [nudgeLoading, setNudgeLoading] = useState(false)
  const [nudgeActionId, setNudgeActionId] = useState<string | null>(null)

  const loadWorkspaces = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await platformAdminApi.listWorkspaces({ limit: 100 })
      setWorkspaces(r.workspaces)
      setTotal(r.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!authed) return
    loadWorkspaces()
    platformAdminApi
      .getPricingUi()
      .then((r) => {
        const ui = r.pricingUi
        setPricingUi(ui)
        setVisibleCsv(ui?.visiblePlanNames?.join(', ') ?? '')
        setHighlightName(ui?.highlightPlanName ?? '')
        setPromoBadge(ui?.promoBadge ?? '')
      })
      .catch(() => {})
    platformAdminApi
      .getAudit(30)
      .then((r) => setAudit(r.entries))
      .catch(() => {})
    platformAdminApi
      .getNudgeStats()
      .then((s) => setNudgeStats(s))
      .catch(() => {})
  }, [authed, loadWorkspaces])

  const loadNudgeList = useCallback(async (tab: typeof nudgeTab) => {
    setNudgeLoading(true)
    setError(null)
    try {
      const r = await platformAdminApi.listPlatformNudges(tab)
      setNudgeList(r.nudges)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load nudges')
    } finally {
      setNudgeLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!authed) return
    void loadNudgeList(nudgeTab)
  }, [authed, nudgeTab, loadNudgeList])

  async function refreshNudgeData() {
    try {
      const s = await platformAdminApi.getNudgeStats()
      setNudgeStats(s)
    } catch {
      /* ignore */
    }
    await loadNudgeList(nudgeTab)
  }

  async function approveNudge(id: string) {
    setNudgeActionId(id)
    setError(null)
    try {
      await platformAdminApi.patchPlatformNudge(id, { status: 'approved' })
      await refreshNudgeData()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approve failed')
    } finally {
      setNudgeActionId(null)
    }
  }

  async function rejectNudge(id: string) {
    setNudgeActionId(id)
    setError(null)
    try {
      await platformAdminApi.patchPlatformNudge(id, { status: 'rejected', review_notes: 'Rejected from platform admin' })
      await refreshNudgeData()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reject failed')
    } finally {
      setNudgeActionId(null)
    }
  }

  function login(e: React.FormEvent) {
    e.preventDefault()
    setPlatformAdminToken(tokenInput.trim())
    setAuthed(true)
    setTokenInput('')
    setError(null)
  }

  function logout() {
    clearPlatformAdminToken()
    setAuthed(false)
    setWorkspaces([])
    setDetail(null)
  }

  async function expandRow(orgId: string) {
    if (expanded === orgId) {
      setExpanded(null)
      setDetail(null)
      return
    }
    setExpanded(orgId)
    setDetail(null)
    setError(null)
    try {
      const r = await platformAdminApi.getWorkspace(orgId)
      setDetail(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed')
    }
  }

  async function savePlan(orgId: string, plan: string) {
    setError(null)
    try {
      await platformAdminApi.patchWorkspace(orgId, { plan })
      await loadWorkspaces()
      if (detail?.workspace.id === orgId) {
        const r = await platformAdminApi.getWorkspace(orgId)
        setDetail(r)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  async function savePricingUi() {
    setError(null)
    const visiblePlanNames = visibleCsv
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    const body: PricingUiConfig = {
      visiblePlanNames: visiblePlanNames.length ? visiblePlanNames : undefined,
      highlightPlanName: highlightName.trim() || null,
      promoBadge: promoBadge.trim() || null,
    }
    try {
      const r = await platformAdminApi.patchPricingUi(body)
      setPricingUi(r.pricingUi)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    }
  }

  async function clearPricingUi() {
    setError(null)
    try {
      await platformAdminApi.patchPricingUi({})
      setVisibleCsv('')
      setHighlightName('')
      setPromoBadge('')
      setPricingUi(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Clear failed')
    }
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-warm-base">
        <form onSubmit={login} className="w-full max-w-md space-y-4 bg-warm-card border border-warm-border rounded-2xl p-8 shadow-card">
          <h1 className="text-xl font-bold text-ink">Duva platform admin</h1>
          <p className="text-sm text-ink-muted">
            Enter the <code className="text-xs bg-warm-inset px-1 rounded">PLATFORM_ADMIN_SECRET</code> value (Bearer token).
          </p>
          <input
            type="password"
            autoComplete="off"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            className="w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
            placeholder="Platform admin token"
          />
          <button
            type="submit"
            className="w-full py-2 rounded-full text-white text-sm font-semibold"
            style={{ background: '#2A7A8A' }}
          >
            Continue
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-warm-base p-6 md:p-10 text-ink">
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-bold">Platform admin</h1>
          <button
            type="button"
            onClick={logout}
            className="text-sm text-ink-muted underline"
          >
            Clear token
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-2">{error}</div>
        )}

        <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-semibold">Nudge templates</h2>
            <button
              type="button"
              onClick={() => void refreshNudgeData()}
              className="text-xs text-brand-teal font-medium"
            >
              Refresh
            </button>
          </div>
          {nudgeStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border">
                <p className="text-ink-muted">Active (approved)</p>
                <p className="text-lg font-semibold text-ink">{nudgeStats.totalApproved}</p>
              </div>
              <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border">
                <p className="text-ink-muted">Total rows</p>
                <p className="text-lg font-semibold text-ink">{nudgeStats.totalAll}</p>
              </div>
              <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border col-span-2">
                <p className="text-ink-muted mb-1">By status</p>
                <p className="text-ink font-mono">
                  {(nudgeStats.byStatus ?? [])
                    .map((r) => `${r.status}: ${r.count}`)
                    .join(' · ') || '—'}
                </p>
              </div>
              <div className="bg-warm-inset rounded-lg px-3 py-2 border border-warm-border col-span-2 sm:col-span-4">
                <p className="text-ink-muted mb-1">Approved by category</p>
                <p className="text-ink text-[11px]">
                  {(nudgeStats.approvedByCategory ?? [])
                    .map((r) => `${r.category}: ${r.count}`)
                    .join(' · ') || '—'}
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2 border-b border-warm-border pb-2">
            {(['pending_review', 'approved', 'rejected', 'draft'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setNudgeTab(t)}
                className={`text-xs px-3 py-1 rounded-full border ${
                  nudgeTab === t
                    ? 'bg-brand-teal text-white border-brand-teal'
                    : 'border-warm-border text-ink-muted hover:bg-warm-inset'
                }`}
              >
                {t.replace('_', ' ')}
              </button>
            ))}
          </div>
          {nudgeLoading ? (
            <p className="text-xs text-ink-muted">Loading…</p>
          ) : (
            <ul className="space-y-3 max-h-96 overflow-auto text-xs">
              {nudgeList.length === 0 && <li className="text-ink-muted">No nudges in this queue.</li>}
              {nudgeList.map((n) => {
                const id = String(n.id)
                return (
                  <li
                    key={id}
                    className="border border-warm-border rounded-lg p-3 bg-warm-inset/40 space-y-1"
                  >
                    <div className="flex flex-wrap justify-between gap-2">
                      <span className="font-semibold text-ink">{String(n.title)}</span>
                      <span className="text-ink-muted capitalize">{String(n.category)}</span>
                    </div>
                    <p className="text-ink-muted whitespace-pre-wrap">{String(n.body)}</p>
                    {n.science_source != null && String(n.science_source).trim() !== '' ? (
                      <p className="text-[10px] text-ink-soft">Source: {String(n.science_source)}</p>
                    ) : null}
                    {nudgeTab === 'pending_review' && (
                      <div className="flex gap-2 pt-2">
                        <button
                          type="button"
                          disabled={nudgeActionId === id}
                          onClick={() => void approveNudge(id)}
                          className="px-3 py-1 rounded-full text-white text-[11px] font-medium disabled:opacity-50"
                          style={{ background: '#2A7A8A' }}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={nudgeActionId === id}
                          onClick={() => void rejectNudge(id)}
                          className="px-3 py-1 rounded-full text-[11px] border border-warm-border disabled:opacity-50"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card">
          <h2 className="text-sm font-semibold mb-3">Pricing page overrides</h2>
          <p className="text-xs text-ink-muted mb-4">
            Optional. Leave visible plans empty to show all tiers. Plan names: Free, Starter, Growth, Enterprise (comma-separated).
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block text-xs">
              <span className="text-ink-muted">Visible plan names (CSV)</span>
              <input
                value={visibleCsv}
                onChange={(e) => setVisibleCsv(e.target.value)}
                className="mt-1 w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
                placeholder="Free, Starter, Growth, Enterprise"
              />
            </label>
            <label className="block text-xs">
              <span className="text-ink-muted">Highlight plan name</span>
              <input
                value={highlightName}
                onChange={(e) => setHighlightName(e.target.value)}
                className="mt-1 w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
                placeholder="Growth"
              />
            </label>
            <label className="block text-xs md:col-span-2">
              <span className="text-ink-muted">Promo badge (short text)</span>
              <input
                value={promoBadge}
                onChange={(e) => setPromoBadge(e.target.value)}
                className="mt-1 w-full bg-warm-inset border border-warm-border rounded-lg px-3 py-2 text-sm"
                placeholder="Founding teams — extra seats"
              />
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={savePricingUi}
              className="px-4 py-2 rounded-full text-sm font-semibold text-white"
              style={{ background: '#2A7A8A' }}
            >
              Save pricing UI
            </button>
            <button type="button" onClick={clearPricingUi} className="px-4 py-2 rounded-full text-sm border border-warm-border">
              Reset to defaults
            </button>
          </div>
          {pricingUi && (
            <pre className="mt-3 text-[10px] bg-warm-inset p-2 rounded overflow-auto">{JSON.stringify(pricingUi, null, 2)}</pre>
          )}
        </section>

        <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold">Workspaces ({total})</h2>
            <button
              type="button"
              onClick={loadWorkspaces}
              disabled={loading}
              className="text-xs text-brand-teal font-medium"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-ink-muted border-b border-warm-border">
                <th className="pb-2 pr-2">Name</th>
                <th className="pb-2 pr-2">Plan</th>
                <th className="pb-2 pr-2">Members</th>
                <th className="pb-2 pr-2">Country</th>
                <th className="pb-2 pr-2">Industry</th>
                <th className="pb-2 pr-2">Last activity</th>
                <th className="pb-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map((w) => {
                const id = String(w.id)
                return (
                  <Fragment key={id}>
                    <tr className="border-b border-warm-border/60">
                      <td className="py-2 pr-2 font-medium">
                        <button type="button" className="text-left underline text-brand-teal" onClick={() => expandRow(id)}>
                          {String(w.name ?? id)}
                        </button>
                      </td>
                      <td className="py-2 pr-2 capitalize">{String(w.plan ?? '')}</td>
                      <td className="py-2 pr-2">{String(w.live_member_count ?? w.employee_count ?? '—')}</td>
                      <td className="py-2 pr-2">{String(w.signup_country ?? '—')}</td>
                      <td className="py-2 pr-2">{String(w.industry ?? '—')}</td>
                      <td className="py-2 pr-2">{w.last_activity_at ? String(w.last_activity_at).slice(0, 10) : '—'}</td>
                      <td className="py-2">{String(w.created_at ?? '').slice(0, 10)}</td>
                    </tr>
                    {expanded === id && detail && (
                      <tr>
                        <td colSpan={7} className="pb-4 pt-0 bg-warm-inset/50 px-3 py-3">
                          <div className="flex flex-wrap gap-3 items-center mb-3">
                            <span className="text-ink-muted">Set plan:</span>
                            {PLANS.map((p) => (
                              <button
                                key={p}
                                type="button"
                                onClick={() => savePlan(id, p)}
                                className="px-2 py-1 rounded border border-warm-border text-[11px] capitalize hover:bg-warm-card"
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                          <p className="text-ink-muted mb-2">{detail.members.length} members</p>
                          <ul className="max-h-40 overflow-auto text-[11px] space-y-1">
                            {detail.members.map((m) => (
                              <li key={String(m.id)}>
                                {String(m.name)} — {String(m.email)} ({String(m.role)})
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </section>

        <section className="bg-warm-card border border-warm-border rounded-2xl p-6 shadow-card">
          <h2 className="text-sm font-semibold mb-3">Recent audit</h2>
          <ul className="text-[11px] space-y-2 font-mono text-ink-muted max-h-48 overflow-auto">
            {audit.map((a) => (
              <li key={String(a.id)}>
                {String(a.created_at)} — {String(a.action)} {String(a.resource_type)}{' '}
                {a.resource_id ? String(a.resource_id) : ''}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  )
}
